import type { GetProductQuery, Category as CategoryType } from 'generated/graphql';
import ProductDetails, { type ProductDetailsProps } from 'components/Product/components/ProductDetails/ProductDetails';
import { Category } from 'components/Category/Category';
import PageMeta from 'components/PageMeta';
import { useServerTranslation } from 'hooks/useServerTranslation';
import { ProductHelper } from 'helpers/ProductHelper';
import { localizedRoutes } from 'constants/localizedRoutes';
import {
    getCategoryBySlug,
    getCategorySlugsByMarket,
    getProductBySku,
    getProductBySlug,
    getRecommendedProducts,
} from 'graphql/productQueries';
import { GetServerSidePropsContext } from 'next';
import { parseContextLocale } from 'utils/parseContextLocale';
import { parseRouteParams } from 'utils/parseRouteParams';
import { MarketCodesEnum } from 'constants/markets';
import Layout from '../Layout/Layout';

export type ProductPageProps = {
    canonicalUrl?: string;
} & (ProductDetailsProps | {
    category: CategoryType;
});

export default function ProductPage(props: ProductPageProps) {
    const { t } = useServerTranslation();

    if ('category' in props) {
        const { category, canonicalUrl } = props;

        return (
            <Layout breadcrumbs={category.breadcrumbs} showBreadcrumbs>
                <PageMeta
                    title={t('seo.title.category', { category: category.title })}
                    description={category.meta_description}
                    keywords={category.meta_keywords}
                    canonicalUrl={canonicalUrl}
                />
                <Category {...category} />
            </Layout>
        );
    }

    const { product, canonicalUrl, ...passProps } = props;

    return (
        <Layout
            breadcrumbs={product?.breadcrumbs ?? undefined}
            showBreadcrumbs
        >
            <PageMeta
                title={t('seo.title.product', { productName: product?.name })}
                description={ProductHelper.getProductDescription(product)}
                type="product"
                canonicalUrl={canonicalUrl}
            />
            <ProductDetails product={product} {...passProps} />
        </Layout>
    );
}

export type ProductPageRouteParams = {
    slug: string[];
};

export async function productPageGetServerSideProps(
    context: GetServerSidePropsContext<ProductPageRouteParams>,
    addLanguagePrefix: boolean = false,
    organisationPersonalWebshopSlug?: string,
) {
    const { market, defaultProductCategorySlug } = await parseContextLocale(context);

    const { data: categorySlugsData } = await getCategorySlugsByMarket(market);
    const categorySlugs = categorySlugsData?.categorySlugs?.items ?? [];
    const slugFullPath = context.params?.slug?.join('/') || defaultProductCategorySlug;
    const isCategoryPage = categorySlugs.includes(slugFullPath);

    const showAllProducts = slugFullPath === defaultProductCategorySlug;

    if (isCategoryPage || showAllProducts) {
        const path = parseRouteParams(context, ['slug'], (slugs) => slugs.join('/'))[0] || defaultProductCategorySlug;

        const { data } = await getCategoryBySlug(market, path, showAllProducts);

        if (!data.category) {
            return { notFound: true };
        }

        const isDefaultPath = path === defaultProductCategorySlug;
        const canonicalUrl = `${localizedRoutes.product.rewrites[market]}/${isDefaultPath ? '' : path}`;

        return { category: data.category, canonicalUrl };
    }

    const [slug] = parseRouteParams(context, ['slug'], (params) => params.slice(-1)[0]);

    const [
        productQuery,
        recommendedDataQuery,
    ] = await Promise.all([
        getProductBySlug(context, market, slug ?? '', organisationPersonalWebshopSlug),
        getRecommendedProducts(market, organisationPersonalWebshopSlug),
        // TODO: recommendedRelated are currently randomized.
        // reimplement this when prioritized.
        // getRecommendedRelatedProducts(market, slug),
    ]);

    const { product } = productQuery;

    if (!product) {
        return { notFound: true };
    }

    const parentWrapperSku = product?.parentWrapperSku ?? null;

    const parentQuery = parentWrapperSku
        ? await getProductBySku(context, market, parentWrapperSku, organisationPersonalWebshopSlug)
        : null;
    const parentProduct = parentQuery?.product;

    const productSlug = cleanProductSlug(product?.slug, parentProduct?.slug);
    const marketPath = localizedRoutes.product.rewrites[market];
    const languagePrefix = addLanguagePrefix && market === MarketCodesEnum.FinlandSwedish ? '/sv' : '';

    const consultantPath = organisationPersonalWebshopSlug
        ? `/consultant/${organisationPersonalWebshopSlug}`
        : '';

    const productUrl = `${languagePrefix}${consultantPath}${marketPath}/${productSlug}`;
    const canonicalUrl = `${languagePrefix}${marketPath}/${productSlug}`;

    if (productUrl !== context.resolvedUrl) {
        return {
            redirect: {
                destination: productUrl,
                permanent: true,
            },
        };
    }

    return {
        product: parentProduct ?? product,
        recommended: recommendedDataQuery.data?.recommended?.items ?? [],
        canonicalUrl,
    };
}

function cleanProductSlug(productSlug?: string | null, parentSlug?: string | null) {
    // If it's a child product we need to use the parents category slug
    const parentCategories = parentSlug?.split('/').slice(0, -1).join('/');
    // Sometimes child products are put under different categories than the parent wrapper.
    // Clear out and remove all redundant categories that are on the children.
    const childWithoutRedundantCategories = productSlug?.split('/').slice(-1)[0];

    return parentCategories
        ? `${parentCategories}/${childWithoutRedundantCategories}`
        : productSlug;
}
